/*
    Where the machine stands on a lacrosse field.

    A racket-sport spot grid anchors the machine to painted lines - "rear axle
    on the baseline". A field has no baseline worth standing on: the four
    racket sports all fit inside 24 m, while a lacrosse field is 110 m long and
    the only findable thing on it is the cage. So these 25 spots are
    GOAL-RELATIVE, laid out on rings a coach can walk to, and every radius is a
    painted marking rather than a round number.

    Frame is the app's physics frame with the drill-end goal at the origin:
    goal centre (0, 0), downfield -y, +x to its right. Bearing phi is measured
    off the downfield axis, so spot(r, phi) = (r sin phi, -r cos phi) and
    bearing 0 is straight out in front of the mouth.

    Derived in code rather than transcribed, which is the whole point of the
    handoff package: change a rulebook number in geometry.ts and the spots
    follow it, instead of drifting from it silently.
*/
import type { CoordWithSys } from "../position-types";

import { arcHalfAngle, DRILL_GEOMETRY, LACROSSE_FIELDS, YD } from "./geometry";

/**
 * How far forward of the stored position the front axle sits.
 *
 * Restated rather than imported from CameraOribitControls, which pulls in
 * THREE and React; spots.test.ts asserts the two agree. Same reason
 * courtLayout.test.ts keeps its own copy.
 */
export const TRAINER_WHEELBASE = 0.8;

/** Deep ring: one long step back off the 12 m fan. */
const DEEP_STEP = 4;

/**
 * The five bearings every outer ring uses, in radians.
 *
 * {-half, -half/2, 0, +half/2, +half} where `half` is the 8 m arc's own
 * half-extent - the angle the rulebook's 45 degree legs fix (see
 * arcHalfAngle). Using the arc's own extent rather than a hand-picked
 * +/-30/+/-60 means the three outer rings are radial step-backs on the SAME
 * five lines: a coach walks straight back 10.6 -> 14.6 -> 18.6 without moving
 * sideways, and no new angular constant enters the codebase.
 */
function ringBearings(): number[] {
    // WOMENS and MIXED share these markings and MENS has none of them, so no
    // spot varies by variant - see DRILL_GEOMETRY.
    const half = arcHalfAngle(LACROSSE_FIELDS.WOMENS);
    if (half == null) {
        throw new Error("lacrosse arc geometry is missing its 45 degree legs");
    }
    return [-half, -half / 2, 0, half / 2, half];
}

/**
 * Round at the storage boundary, so stored values are stable and comparable.
 *
 * The `+ 0` normalises -0 to 0. The GLE ring sits at bearing +/-PI/2, where
 * `-d * cos(bearing)` is -0, and that would persist to the database as "-0".
 */
function round3(value: number): number {
    return Math.round(value * 1000) / 1000 + 0;
}

/**
 * Yaw is radians, so it needs finer rounding than the metre values.
 *
 * The `+ 0` normalises -0 to 0. Negating a zero bearing yields -0, which is
 * not `Object.is`-equal to 0 and would persist to the database as "-0".
 */
function round6(value: number): number {
    return Math.round(value * 1e6) / 1e6 + 0;
}

/**
 * Body yaw that points the machine at the cage, for a spot at `bearing`.
 *
 * Yaw 0 faces physics +y, and the machine's heading is `(sin yaw, cos yaw)` -
 * which is what CameraOribitControls' frontAxlePosition(), the visualizer's
 * trainer rotation and the 2D picker's marker rotation all read. A spot at
 * bearing b sits at `(d sin b, -d cos b)`, so facing back at the goal means
 * heading `(-sin b, cos b)`, i.e. simply:
 *
 *     yaw = -bearing
 *
 * For the SHOOTING rings only - the ones downfield of the goal line. It also
 * makes the wheelbase convention land: the front axle sits one wheelbase along
 * the heading, so facing the cage makes that offset radial and the axle lands
 * exactly on the ring radius. At yaw 0 the offset would run up the +y axis and
 * the deep corner spot would miss 18.6 m by 35 cm.
 */
function yawFacingCage(bearing: number): number {
    /*
        Clamped to +/-PI after rounding, not before. Rounding a bearing of PI to
        six decimals gives 3.141593, whose magnitude is very slightly MORE than
        PI - 180.0000198 degrees - and the yaw slider's range is exactly +/-180,
        so such a value would be one the control silently rewrites. Same class
        of bug as the old +/-75 clamp rewriting a 90 degree spot.
    */
    return Math.max(-Math.PI, Math.min(Math.PI, round6(-bearing)));
}

/**
 * Body yaw that points the machine down the field, for a spot at `x`.
 *
 * Used by every spot ON or BEHIND the goal line - the GLE ring and the pair
 * behind the cage. From there a coach feeds OUT into play: aiming at the cage
 * would fire straight across the mouth from the GLE, or into the back of the
 * net from behind it. So these face downfield, and the slider is how a drill
 * angles them off that.
 *
 * Straight downfield is heading (0, -1), which is yaw +/-180 - the same
 * direction either way. The SIGN is chosen by which side of the goal the spot
 * sits on, so the slider has room to swing the machine INWARD, towards the
 * middle of the field, without running off the end of its range: a left-side
 * spot starts at +180 and angles in as the value falls, a right-side spot
 * starts at -180 and angles in as it rises.
 *
 * No shipped spot on or behind the goal line sits at x = 0, so the tie is
 * academic; it resolves to +180.
 */
function yawFacingField(x: number): number {
    return x <= 0 ? Math.PI : -Math.PI;
}

/**
 * The body yaw a lacrosse spot is meant to be stood at, from its position.
 *
 * The one implementation of the facing rule. The spot table builds its stored
 * yaw from this, and the yaw slider uses it as the ZERO its offsets are
 * measured from - so a coach adjusts "20 degrees off where this spot aims"
 * rather than setting an absolute body angle whose zero has nothing to do with
 * where the machine is standing.
 *
 * Deriving it from x and y rather than storing a second field means it is still
 * right for a workout whose yaw was hand-adjusted and saved: the reference is a
 * property of the SPOT, not of the current aim.
 */
export function lacrosseDefaultYaw(x: number, y: number): number {
    return y >= 0 ? yawFacingField(x) : yawFacingCage(Math.atan2(x, -y));
}

/** Fold a yaw into (-PI, PI], the range a stored body yaw lives in. */
export function normaliseYaw(radians: number): number {
    let v = radians;
    while (v <= -Math.PI) v += 2 * Math.PI;
    while (v > Math.PI) v -= 2 * Math.PI;
    return v + 0;
}

/**
 * Label for the module apps' yaw radio, which reads yawDescription.
 *
 * A bearing past +/-90 degrees puts the spot behind the goal line, where
 * "facing the cage" and "facing the field" are the same direction - the goal
 * is between the machine and the play. Saying the latter is more use to a
 * coach standing there.
 */
function yawLabel(yaw: number, facingField: boolean): string | undefined {
    if (yaw === 0) return undefined; // ModuleTrainerPosition renders "Forward"
    const deg = Math.round((yaw * 180) / Math.PI);
    const towards = facingField ? "facing down the field" : "facing the cage";
    return `${deg > 0 ? "+" : ""}${deg}° - ${towards}`;
}

/**
 * A spot's STORED value, given where its front axle should land.
 *
 * The stored position is the rear axle. The repo convention is that the front
 * axle goes on the painted line and the stored value is the line plus a
 * wheelbase - tennis stores -12.69 so its front axle lands on the -11.885 m
 * baseline, and padel stores -7.8 so its front axle lands exactly on the 7.0 m
 * service line. So the machine is pushed one wheelbase radially OUTWARD from
 * the goal, away from the direction it faces.
 */
/**
 * A spot at a stated STORED position - the rear axle, as the database holds it.
 *
 * Which way it faces is decided by which side of the goal LINE it sits on, not
 * by its ring. Downfield (y < 0) is a shooting position and aims at the cage;
 * on or behind the line it feeds out into the field instead. The yaw comes from
 * the ROUNDED position, so the table and the slider's reference agree exactly
 * rather than to within a rounding error.
 */
function spotAt(x: number, y: number): CoordWithSys {
    const facingField = y >= 0;
    const yaw = lacrosseDefaultYaw(x, y);
    const description = yawLabel(yaw, facingField);
    return {
        x,
        y,
        yaw,
        // Spread, not `yawDescription: undefined`: an explicit undefined key
        // still shows up in equality checks and in anything that serialises.
        ...(description === undefined ? {} : { yawDescription: description }),
        sys: "physics",
    };
}

/**
 * A spot on a ring, pushed one wheelbase radially outward so the FRONT axle
 * lands on the marking at `r`. See the note on yawFacingCage.
 */
function stored(r: number, bearing: number): CoordWithSys {
    const d = r + TRAINER_WHEELBASE;
    return spotAt(
        round3(d * Math.sin(bearing)),
        round3(-d * Math.cos(bearing)),
    );
}

/** As `stored`, but from a front-axle point rather than a radius and bearing. */
function storedAt(x: number, y: number): CoordWithSys {
    const r = Math.hypot(x, y);
    return stored(r, Math.atan2(x, -y));
}

/*
    ------------------------------------------------------------------------
    The TWO-CAGE drill.

    A different placement problem, so a different control rather than more
    spots. Two cages straddle the centreline 8 ft apart (see TWO_CAGE_GAP) and
    the machine stands on the line that bisects them, shooting at the pair - so
    the only free variable is how far back it stands. One axis, and the spec is
    "anywhere from 15 yards back", so it is a continuous range and the picker
    offers a slider instead of markers.

    x is therefore always 0 and the yaw always 0: straight up the bisector at
    the cages, which is what lacrosseDefaultYaw already returns for any spot on
    the centreline downfield of the goal line. The two-cage mode adds no new
    facing rule - it just narrows where the machine may be.
    ------------------------------------------------------------------------
*/

/**
 * How far back the machine may stand in the two-cage drill, in YARDS, measured
 * from the goal line to the FRONT axle.
 *
 * Yards because that is how the drill is called - "put it ten back" - and
 * because both ends of the range are whole yards on a field laid out in them.
 * Metres are still what gets stored; see lacrosseTwoCageSpot.
 *
 * `near` is 3 yd rather than 0 for a physical reason: facing the cages, a
 * machine level with them is aimed at the gap between them and cannot shoot
 * either. 3 yd is the nearest whole yard that works, and it is also the men's
 * crease radius, so it is a painted distance rather than a chosen one -
 * spots.test.ts asserts that. From there the cages sit 38 degrees either side
 * of straight ahead, well inside the machine's pan.
 *
 * `step` is half a yard. Finer than a coach can place a machine by eye, which
 * is the point of "anywhere".
 */
export const LACROSSE_TWO_CAGE_YARDS = {
    near: 3,
    far: 15,
    step: 0.5,
    /** Where the slider lands when the drill is first switched on. */
    default: 10,
} as const;

/** Clamp a two-cage distance, in yards, into the range above. */
export function clampTwoCageYards(yards: number): number {
    const { near, far } = LACROSSE_TWO_CAGE_YARDS;
    return Math.max(near, Math.min(far, yards));
}

/**
 * The two-cage spot at a given distance back, in yards.
 *
 * Stored value is the rear axle, as everywhere: the machine faces the cages,
 * so its heading is +y and the wheelbase pushes the stored position one
 * wheelbase FURTHER back than the front axle. Built through spotAt() so the
 * yaw comes from the one facing rule rather than a second copy of it.
 */
export function lacrosseTwoCageSpot(yards: number): CoordWithSys {
    const front = clampTwoCageYards(yards) * YD;
    return spotAt(0, round3(-(front + TRAINER_WHEELBASE)));
}

/**
 * The distance back, in yards, a stored two-cage position represents.
 *
 * The inverse of lacrosseTwoCageSpot, so the slider can be seeded from a saved
 * workout rather than resetting to the default every time the dialog opens.
 */
export function lacrosseTwoCageYards(y: number): number {
    return clampTwoCageYards((-y - TRAINER_WHEELBASE) / YD);
}

function buildSpots(): CoordWithSys[] {
    const { goalCircleR, arcR, fanR, dotBack } = DRILL_GEOMETRY;
    const bearings = ringBearings();
    const spots: CoordWithSys[] = [];

    /*
        Ring 1 - behind the cage, 6 spots.

        A straight row on the below-goal dot line, 5 yd behind the goal line.
        These all face straight down the field, so their wheelbase offset is
        purely in y: storing them one wheelbase BEHIND the dot line puts every
        front axle exactly on it, and the inner pair's on the painted dots at
        +/-goalCircleR.

        The lateral spread reuses the arc and fan radii rather than inventing
        offsets, so this row lines up with the GLE ring directly in front of it.

        THERE IS DELIBERATELY NO CENTRE SPOT. A machine dead behind the cage
        fires into the back of the net, so it would only work for a lofted feed
        over the 1.83 m crossbar; our subject-matter expert ruled it out. Every
        spot here clears the posts, whose half-width is 0.915 m.
    */
    const dotRow = dotBack + TRAINER_WHEELBASE;
    for (const x of [-fanR, -arcR, -goalCircleR, goalCircleR, arcR, fanR]) {
        spots.push(spotAt(round3(x), round3(dotRow)));
    }

    /*
        Ring 2 - the GLE, or goal line extended, 5 spots.

        Level with the mouth at y = 0, out at the arc and fan radii. Both land
        on the 12 m fan's flat side, which runs along the GLE from the goal
        circle out to 14.6 - that side's far endpoint.

        These face DOWN THE FIELD, not at the cage. Level with the goal line,
        aiming at the cage would fire straight across the mouth, and a coach on
        the GLE is feeding out into play. Same as the behind-the-cage pair, and
        the reason the yaw range has to reach 180.
    */
    const gle = [-fanR, -arcR, arcR, fanR].map((x) => storedAt(x, 0));

    /*
        ... plus the GLE's centre: the machine standing IN the goal mouth,
        feeding out into the field. Stored (0, 0) puts the rear axle on the goal
        line with the body straddling the mouth, which is 1.83 m wide against a
        machine about 1.2 m across.

        Stated outright rather than built from a radius, because it is the one
        spot with no radius - at the origin there is no bearing to push along.
        It is also the only place yawFacingField's x = 0 tie is reachable, and
        it resolves to +180: down the field, the only sensible direction from
        inside the cage.
    */
    gle.push(spotAt(0, 0));

    // Sorted so the row reads left to right and the mouth sits in its middle.
    gle.sort((a, b) => a.x - b.x);
    spots.push(...gle);

    // Rings 3 and 4 - the 8 m arc and the 12 m fan.
    for (const r of [arcR, fanR]) {
        for (const bearing of bearings) {
            spots.push(stored(r, bearing));
        }
    }

    /*
        Ring 5 - deep, 4 spots.

        No straight-on spot: ring 4 already has a centre 4 m nearer, and
        widening the deep row opens the shooting angle rather than repeating a
        feed the next ring in already gives. Dropping the centre also keeps the
        default index on the fan's centre.
    */
    for (const bearing of bearings.filter((b) => b !== 0)) {
        spots.push(stored(fanR + DEEP_STEP, bearing));
    }

    return spots;
}

/**
 * The 25 goal-relative trainer positions, 6/5/5/5/4 by ring.
 *
 * ONE frozen array for all three variants. Nothing a spot uses varies by
 * variant: goalCircleR, arcR, fanR and the arc's half-extent are identical in
 * WOMENS and MIXED, and MENS simply has none of them - so a men's field gets
 * the arc and the fan as dashed drill rings and the coordinates stay put.
 *
 * Every spot is inside the field of play in all three variants. The binding
 * constraint is NOT the sidelines - the widest is 15.9 m against a men's
 * 27.4 m half-width - but the behind-the-cage ring against the women's
 * goal-line-to-end-line distance, which is 9.144 m where a men's is 13.716.
 *
 * Each spot also carries the body YAW it should be stood at. The rings
 * downfield of the goal line face the CAGE, because they are shooting
 * positions; the GLE ring and the pair behind the cage face DOWN THE FIELD,
 * because from there a coach feeds out into play. See yawFacingCage() and
 * yawFacingField().
 *
 * These values persist into `workout_apps.position_x/y` and `position_yaw`, so
 * they must not be gated on a feature flag and must not move once a workout has
 * been authored against them.
 */
export const LACROSSE_SPOTS: readonly CoordWithSys[] =
    Object.freeze(buildSpots());

/**
 * Default spot: the fan centre.
 *
 * Front axle exactly on the 12 m fan, 14.6 m (16 yd) dead in front of the
 * mouth, stored (0, -15.4).
 */
export const LACROSSE_DEFAULT_SPOT_INDEX = 18;

/**
 * Ring sizes, in the order LACROSSE_SPOTS emits them.
 *
 * Exported for the tests rather than for the app: the counts are what pin the
 * default index, so they are worth stating once instead of recounting.
 */
export const LACROSSE_RING_SIZES = [6, 5, 5, 5, 4] as const;
